import { FirmaIntermediario } from "./FirmaIntermediario";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MupLayout } from "@/pages/mup/MupLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { SiRegolePanel } from "@/components/ai/SiRegolePanel";
import { Package, Building2, Users, FileCheck2, Plus, Pencil, CheckCircle2, Trash2, Layers, Upload } from "lucide-react";
import { fmtIT } from "@/lib/dateIT";

export default function MupCatalogo() {
  return (
    <MupLayout subtitle="Amministrazione" title="Catalogo & Compliance">
      <Tabs defaultValue="products">
        <TabsList className="bg-white border border-slate-200 mb-6">
          <TabsTrigger value="products" data-testid="cat-tab-products" className="gap-2"><Package className="h-4 w-4" /> Prodotti</TabsTrigger>
          <TabsTrigger value="aree" data-testid="cat-tab-aree" className="gap-2"><Layers className="h-4 w-4" /> Aree di Tutela</TabsTrigger>
          <TabsTrigger value="companies" data-testid="cat-tab-companies" className="gap-2"><Building2 className="h-4 w-4" /> Compagnie</TabsTrigger>
          <TabsTrigger value="intermediaries" data-testid="cat-tab-intermediaries" className="gap-2"><Users className="h-4 w-4" /> Intermediari</TabsTrigger>
          <TabsTrigger value="templates" data-testid="cat-tab-templates" className="gap-2"><FileCheck2 className="h-4 w-4" /> Template (Compliance)</TabsTrigger>
          <TabsTrigger value="si-regole" data-testid="cat-tab-si-regole" className="gap-2"><Package className="h-4 w-4" /> Regole Smart Import</TabsTrigger>
        </TabsList>
        <TabsContent value="products"><ProductsPanel /></TabsContent>
        <TabsContent value="aree"><AreePanel /></TabsContent>
        <TabsContent value="companies"><CompaniesPanel /></TabsContent>
        <TabsContent value="intermediaries"><IntermediariesPanel /></TabsContent>
        <TabsContent value="templates"><TemplatesPanel /></TabsContent>
        <TabsContent value="si-regole"><SiRegolePanel /></TabsContent>
      </Tabs>
    </MupLayout>
  );
}

const Section = ({ title, sub, onAdd, children }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="font-heading text-lg text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500">{sub}</p>
      </div>
      {onAdd && <Button data-testid="cat-add-btn" onClick={onAdd} className="brand-gradient text-white border-0"><Plus className="h-4 w-4 mr-2" /> Aggiungi</Button>}
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-slate-600 text-sm">{label}</Label>{children}</div>
);

/* ---------------- Products ---------------- */
function ProductsPanel() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);

  const load = () => {
    api.get("/mup/products").then(({ data }) => setItems(data));
    api.get("/mup/companies").then(({ data }) => setCompanies(data));
    api.get("/mup/templates").then(({ data }) => setTemplates(data));
    api.get("/mup/aree-tutela").then(({ data }) => setAreas(data));
  };
  useEffect(() => { load(); }, []);

  const empty = { company_id: "", branch: "", family: "", name: "", code: "", description: "", is_investment: false, grid_rows: [], mandatory_docs: [], area_ids: [], template_id: "", active: true };
  const openNew = () => { setF({ ...empty }); setOpen(true); };
  const openEdit = (p) => { setF({ ...p, area_ids: p.area_ids || [], grid_text: (p.grid_rows || []).join("\n") }); setOpen(true); };

  const save = async () => {
    const payload = { ...f, area_ids: f.area_ids || [], grid_rows: (f.grid_text ?? (f.grid_rows || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean) };
    delete payload.grid_text; delete payload.product_id; delete payload.created_at;
    try {
      if (f.product_id) await api.put(`/mup/products/${f.product_id}`, payload);
      else await api.post("/mup/products", payload);
      toast.success("Prodotto salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Eliminare il prodotto "${p.name}"?`)) return;
    try { await api.delete(`/mup/products/${p.product_id}`); toast.success("Prodotto eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [impOpen, setImpOpen] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impPreview, setImpPreview] = useState(null);
  const [impBusy, setImpBusy] = useState(false);

  const runImport = async (apply) => {
    if (!impFile) { toast.error("Seleziona un file"); return; }
    setImpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", impFile);
      fd.append("dry_run", apply ? "false" : "true");
      const { data } = await api.post("/mup/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (apply) {
        toast.success(`Import completato: ${data.creati} prodotti creati`);
        setImpOpen(false); setImpFile(null); setImpPreview(null); load();
      } else {
        setImpPreview(data);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImpBusy(false); }
  };

  return (
    <Section title="Prodotti" sub={`${items.length} prodotti`} onAdd={openNew}>
      <div className="mb-4">
        <Button variant="outline" data-testid="cat-product-import-btn" onClick={() => { setImpOpen(true); setImpPreview(null); setImpFile(null); }}>
          <Upload className="h-4 w-4 mr-2" /> Importa Excel/CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.product_id} data-testid={`cat-product-${p.product_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-heading font-medium text-slate-900">{p.name}{p.active === false && <span className="ml-2 text-[10px] text-slate-400">(non attivo)</span>}</h4>
              <div className="flex">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => del(p)} data-testid={`cat-product-del-${p.product_id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-2">{p.branch} · {p.family}</p>
            {(p.area_ids || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {p.area_ids.map((aid) => {
                  const a = areas.find((x) => x.area_id === aid);
                  return a ? <Badge key={aid} className="text-[10px] bg-blue-100 text-brand-blue border-0">{a.titolo}</Badge> : null;
                })}
              </div>
            )}
            <div className="flex flex-wrap gap-1">
              {(p.grid_rows || []).slice(0, 4).map((g, i) => <Badge key={i} variant="outline" className="text-[10px]">{g}</Badge>)}
              {(p.grid_rows || []).length > 4 && <Badge variant="outline" className="text-[10px]">+{p.grid_rows.length - 4}</Badge>}
            </div>
          </div>
        ))}
      </div>
      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{f.product_id ? "Modifica" : "Nuovo"} prodotto</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Field label="Nome commerciale"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ramo"><Input value={f.branch} onChange={(e) => setF({ ...f, branch: e.target.value })} /></Field>
                <Field label="Codice"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></Field>
              </div>
              <Field label="Compagnia">
                <select value={f.company_id} onChange={(e) => setF({ ...f, company_id: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm">
                  <option value="">—</option>
                  {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Template MUP collegato">
                <select value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm">
                  <option value="">—</option>
                  {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name} v{t.version} {t.approved ? "✓" : "(non approvato)"}</option>)}
                </select>
              </Field>
              <Field label="Voci griglia esigenze (una per riga)">
                <Textarea rows={6} value={f.grid_text ?? (f.grid_rows || []).join("\n")} onChange={(e) => setF({ ...f, grid_text: e.target.value })} />
              </Field>
              <Field label="Aree di Tutela collegate">
                <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 p-3">
                  {areas.length === 0 && <span className="text-xs text-slate-400">Nessuna area disponibile</span>}
                  {areas.map((a) => {
                    const checked = (f.area_ids || []).includes(a.area_id);
                    return (
                      <label key={a.area_id} data-testid={`cat-product-area-${a.area_id}`}
                        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${checked ? "border-brand-blue bg-blue-50 text-brand-blue" : "border-slate-200 text-slate-600"}`}>
                        <input type="checkbox" className="hidden" checked={checked}
                          onChange={(e) => setF((prev) => ({
                            ...prev,
                            area_ids: e.target.checked
                              ? [...(prev.area_ids || []), a.area_id]
                              : (prev.area_ids || []).filter((x) => x !== a.area_id),
                          }))} />
                        {a.titolo}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="cat-product-active" /> Attivo (visibile in compilazione MUP)
              </label>
            </div>
            <DialogFooter><Button onClick={save} disabled={!f.name} className="brand-gradient text-white border-0">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Importa prodotti da Excel/CSV</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">Colonne attese (intestazione): <b>Compagnia</b>, <b>Prodotto</b>, <b>Ramo</b>, <b>Garanzie</b> (voci separate da <code>;</code> o a capo), <b>Area</b> (opzionale, titolo dell'area di tutela). Le compagnie mancanti verranno create.</p>
            <Button variant="outline" size="sm" data-testid="cat-import-template-btn"
              onClick={async () => {
                try {
                  const res = await api.get("/mup/products/import-template", { responseType: "blob" });
                  const url = window.URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = "modello_import_prodotti.xlsx"; a.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) { toast.error("Errore download modello"); }
              }}>
              <Upload className="h-4 w-4 mr-2 rotate-180" /> Scarica modello Excel
            </Button>
            <input type="file" accept=".xlsx,.xls,.csv" data-testid="cat-import-file"
              onChange={(e) => { setImpFile(e.target.files?.[0] || null); setImpPreview(null); }}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm" />
            {impPreview && (
              <div className="rounded-lg border border-slate-200 p-3 text-sm space-y-1">
                <div className="flex gap-4">
                  <span>Totale righe: <b>{impPreview.totale}</b></span>
                  <span className="text-emerald-600">Da creare: <b>{impPreview.creabili}</b></span>
                  <span className="text-slate-500">Già presenti: <b>{impPreview.gia_presenti}</b></span>
                </div>
                {impPreview.nuove_compagnie?.length > 0 && (
                  <p className="text-xs text-amber-600">Nuove compagnie: {impPreview.nuove_compagnie.join(", ")}</p>
                )}
                <div className="max-h-48 overflow-y-auto mt-2 border-t border-slate-100 pt-2">
                  {(impPreview.preview || []).slice(0, 30).map((r, i) => (
                    <div key={i} className="text-xs py-0.5 flex justify-between gap-2">
                      <span className="truncate">{r.compagnia || "—"} · <b>{r.nome}</b> {r.voci?.length ? `(${r.voci.length} garanzie)` : ""}</span>
                      <span className={r.esiste ? "text-slate-400" : "text-emerald-600"}>{r.esiste ? "esiste" : "nuovo"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {!impPreview ? (
              <Button data-testid="cat-import-preview-btn" onClick={() => runImport(false)} disabled={impBusy || !impFile} className="brand-gradient text-white border-0">
                {impBusy ? "Analisi…" : "Anteprima"}
              </Button>
            ) : (
              <Button data-testid="cat-import-apply-btn" onClick={() => runImport(true)} disabled={impBusy || impPreview.creabili === 0} className="brand-gradient text-white border-0">
                {impBusy ? "Import…" : `Conferma import (${impPreview.creabili})`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

/* ---------------- Aree di Tutela ---------------- */
function AreePanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const load = () => api.get("/mup/aree-tutela").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setF({ titolo: "", area_tutela: "", voci: [], order: items.length, active: true, voci_text: "" }); setOpen(true); };
  const openEdit = (a) => { setF({ ...a, voci_text: (a.voci || []).join("\n") }); setOpen(true); };
  const save = async () => {
    const payload = {
      titolo: f.titolo, area_tutela: f.area_tutela, order: f.order ?? 0, active: f.active !== false,
      voci: (f.voci_text ?? (f.voci || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (f.area_id) await api.put(`/mup/aree-tutela/${f.area_id}`, payload);
      else await api.post("/mup/aree-tutela", payload);
      toast.success("Area di tutela salvata"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (a) => {
    if (!window.confirm(`Eliminare l'area "${a.titolo}"?`)) return;
    try { await api.delete(`/mup/aree-tutela/${a.area_id}`); toast.success("Area eliminata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Section title="Aree di Tutela" sub={`${items.length} aree · configura le voci (garanzie) della griglia esigenze per ogni area`} onAdd={openNew}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a) => (
          <div key={a.area_id} data-testid={`cat-area-${a.area_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex justify-between items-start mb-1">
              <div>
                <h4 className="font-heading font-medium text-slate-900">{a.titolo}{a.active === false && <span className="ml-2 text-[10px] text-slate-400">(non attiva)</span>}</h4>
                <p className="text-xs text-slate-500">{a.area_tutela}</p>
              </div>
              <div className="flex">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`cat-area-edit-${a.area_id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => del(a)} data-testid={`cat-area-del-${a.area_id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            {(a.voci || []).length > 0 ? (
              <div className="flex flex-wrap gap-1 mt-2">
                {a.voci.slice(0, 5).map((v, i) => <Badge key={i} variant="outline" className="text-[10px]">{v}</Badge>)}
                {a.voci.length > 5 && <Badge variant="outline" className="text-[10px]">+{a.voci.length - 5}</Badge>}
              </div>
            ) : <p className="text-xs text-amber-600 mt-2">Nessuna voce configurata</p>}
          </div>
        ))}
      </div>
      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{f.area_id ? "Modifica" : "Nuova"} area di tutela</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Titolo"><Input value={f.titolo} onChange={(e) => setF({ ...f, titolo: e.target.value })} data-testid="cat-area-titolo" /></Field>
                <Field label="Area di tutela"><Input value={f.area_tutela} onChange={(e) => setF({ ...f, area_tutela: e.target.value })} placeholder="Es. Lavoro/aziende" /></Field>
              </div>
              <Field label="Ordine"><Input type="number" value={f.order ?? 0} onChange={(e) => setF({ ...f, order: parseInt(e.target.value || "0", 10) })} /></Field>
              <Field label="Voci griglia esigenze / garanzie (una per riga)">
                <Textarea rows={7} value={f.voci_text ?? (f.voci || []).join("\n")} onChange={(e) => setF({ ...f, voci_text: e.target.value })} data-testid="cat-area-voci" placeholder="RC Economia Solidale&#10;Infortuni Economia Solidale&#10;…" />
              </Field>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Attiva (selezionabile in compilazione MUP)
              </label>
            </div>
            <DialogFooter><Button onClick={save} disabled={!f.titolo} data-testid="cat-area-save" className="brand-gradient text-white border-0">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Section>
  );
}
function CompaniesPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const load = () => api.get("/mup/companies").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setF({ name: "", code: "", relation_type: "mandato_diretto", active: true, notes: "", email_recessi: "" }); setOpen(true); };
  const openEdit = (c) => { setF({ ...c }); setOpen(true); };
  const save = async () => {
    const payload = { ...f }; const cid = payload.company_id; delete payload.company_id; delete payload.created_at;
    try {
      if (cid) await api.put(`/mup/companies/${cid}`, payload); else await api.post("/mup/companies", payload);
      toast.success("Compagnia salvata"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Eliminare la compagnia "${c.name}"?`)) return;
    try { await api.delete(`/mup/companies/${c.company_id}`); toast.success("Compagnia eliminata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Section title="Compagnie e mandati" sub={`${items.length} compagnie`} onAdd={openNew}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.company_id} data-testid={`cat-company-${c.company_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex justify-between items-start">
            <div><h4 className="font-heading font-medium text-slate-900">{c.name}{c.active === false && <span className="ml-2 text-[10px] text-slate-400">(non attiva)</span>}</h4><p className="text-xs text-slate-500">{c.relation_type} · {c.code}</p></div>
            <div className="flex">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => del(c)} data-testid={`cat-company-del-${c.company_id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{f.company_id ? "Modifica" : "Nuova"} compagnia</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Field label="Ragione sociale"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Codice interno"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></Field>
                <Field label="Tipo rapporto">
                  <select value={f.relation_type} onChange={(e) => setF({ ...f, relation_type: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm">
                    <option value="mandato_diretto">Mandato diretto</option>
                    <option value="collaborazione">Collaborazione</option>
                    <option value="altro">Altro</option>
                  </select>
                </Field>
              </div>
              <Field label="Email/PEC per le comunicazioni di recesso"><Input value={f.email_recessi || ""} onChange={(e) => setF({ ...f, email_recessi: e.target.value })} placeholder="es. recessi@compagnia.it" data-testid="cat-company-email-recessi" /></Field>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="cat-company-active" /> Attiva (selezionabile in compilazione MUP)
              </label>
            </div>
            <DialogFooter><Button onClick={save} disabled={!f.name} className="brand-gradient text-white border-0">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Section>
  );
}

/* ---------------- Intermediaries ---------------- */
function IntermediariesPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const load = () => api.get("/mup/intermediaries").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setF({ name: "", internal_code: "", rui_section: "", rui_number: "", rui_date: "", qualification: "", email: "", phone: "", area: "", active: true }); setOpen(true); };
  const openEdit = (c) => { setF({ ...c }); setOpen(true); };
  const save = async () => {
    const payload = { ...f }; const iid = payload.intermediary_id; delete payload.intermediary_id; delete payload.created_at;
    try {
      if (iid) await api.put(`/mup/intermediaries/${iid}`, payload); else await api.post("/mup/intermediaries", payload);
      toast.success("Intermediario salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Section title="Intermediari" sub={`${items.length} intermediari`} onAdd={openNew}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.intermediary_id} data-testid={`cat-intermediary-${c.intermediary_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2"><h4 className="font-heading font-medium text-slate-900">{c.name}</h4>{c.active ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Attivo</Badge> : <Badge variant="outline" className="text-[10px]">Non attivo</Badge>}</div>
              <p className="text-xs text-slate-500">RUI {c.rui_section} {c.rui_number} · {c.qualification}</p>
              <FirmaIntermediario it={c} onChange={load} />
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{f.intermediary_id ? "Modifica" : "Nuovo"} intermediario</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Field label="Nome e cognome"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sezione RUI"><Input value={f.rui_section} onChange={(e) => setF({ ...f, rui_section: e.target.value })} /></Field>
                <Field label="N. iscrizione RUI"><Input value={f.rui_number} onChange={(e) => setF({ ...f, rui_number: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Qualifica"><Input value={f.qualification} onChange={(e) => setF({ ...f, qualification: e.target.value })} /></Field>
                <Field label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Attivo
              </label>
            </div>
            <DialogFooter><Button onClick={save} disabled={!f.name} className="brand-gradient text-white border-0">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Section>
  );
}

/* ---------------- Templates (Compliance) ---------------- */
function TemplatesPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const load = () => api.get("/mup/templates").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setF({ name: "", version: "1.0", effective_date: "", allegato3_text: "", privacy_text: "", receipt_declarations: [], approved: false }); setOpen(true); };
  const openEdit = (t) => { setF({ ...t, decl_text: (t.receipt_declarations || []).join("\n") }); setOpen(true); };
  const save = async () => {
    const payload = { ...f, receipt_declarations: (f.decl_text ?? (f.receipt_declarations || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean) };
    const tid = payload.template_id;
    delete payload.decl_text; delete payload.template_id; delete payload.created_at; delete payload.approved_by; delete payload.last_revision;
    try {
      if (tid) await api.put(`/mup/templates/${tid}`, payload); else await api.post("/mup/templates", payload);
      toast.success("Template salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Section title="Template regolamentari" sub="I testi normativi (Allegato 3, privacy, ricevuta) NON sono generati dall'app: inseriscili e approvali qui." onAdd={openNew}>
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.template_id} data-testid={`cat-template-${t.template_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-heading font-medium text-slate-900">{t.name}</h4>
                <Badge variant="outline">v{t.version}</Badge>
                {t.approved ? <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle2 className="h-3 w-3 mr-1" /> Approvato</Badge> : <Badge className="bg-amber-100 text-amber-700 border-0">Da approvare</Badge>}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Efficace dal {fmtIT(t.effective_date)} · Approvato da {t.approved_by || "—"} · Ultima revisione {fmtIT(t.last_revision)}
              </p>
              {(!t.allegato3_text || !t.privacy_text) && <p className="text-xs text-amber-600 mt-1">⚠ Testi regolamentari incompleti: il PDF stamperà segnaposto finché non li inserisci.</p>}
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
            <DialogHeader><DialogTitle className="font-heading">{f.template_id ? "Modifica" : "Nuovo"} template</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Nome"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
                <Field label="Versione"><Input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} /></Field>
                <Field label="Data efficacia"><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} /></Field>
              </div>
              <Field label="Testo Allegato 3 (Sezioni I–VIII)"><Textarea rows={5} value={f.allegato3_text} onChange={(e) => setF({ ...f, allegato3_text: e.target.value })} placeholder="Incolla qui il testo regolamentare approvato…" /></Field>
              <Field label="Testo informativa privacy"><Textarea rows={4} value={f.privacy_text} onChange={(e) => setF({ ...f, privacy_text: e.target.value })} /></Field>
              <Field label="Dichiarazioni ricevuta contraente (una per riga)"><Textarea rows={4} value={f.decl_text ?? (f.receipt_declarations || []).join("\n")} onChange={(e) => setF({ ...f, decl_text: e.target.value })} /></Field>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={f.approved} onChange={(e) => setF({ ...f, approved: e.target.checked })} /> Approva questo template (registra utente e data)
              </label>
            </div>
            <DialogFooter><Button onClick={save} disabled={!f.name} className="brand-gradient text-white border-0">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Section>
  );
}
